import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { LOGO } from "@/lib/media";

const links = [
  { to: "/", label: "Home" },
  { to: "/chi-siamo", label: "Chi Siamo" },
  { to: "/servizi", label: "Servizi" },
  { to: "/contatti", label: "Contatti" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-[100] transition-[background-color,border-color,padding] duration-500 ${
        scrolled
          ? "bg-[#F4F2EE]/80 backdrop-blur-xl border-b border-[#1A3322]/10 py-3"
          : "bg-transparent py-5"
      }`}
    >
      <nav className="mx-auto max-w-[1400px] px-4 sm:px-8 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-3 group">
          <img src={LOGO} alt="B FRUIT SRL" className="h-11 w-11 object-contain transition-transform duration-500 group-hover:rotate-6" />
          <span className="font-serif-display text-2xl leading-none tracking-tight text-[#1A3322]">
            B FRUIT <span className="italic font-light text-[#C85A32]">srl</span>
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-9">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `link-underline text-sm uppercase tracking-[0.18em] font-medium transition-colors duration-300 ${
                  isActive ? "text-[#C85A32]" : "text-[#1A3322]/80 hover:text-[#1A3322]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <Link
            to="/contatti"
            data-testid="nav-cta"
            className="group inline-flex items-center gap-2 rounded-full bg-[#1A3322] text-[#F4F2EE] pl-5 pr-4 py-2.5 text-sm font-medium transition-colors duration-300 hover:bg-[#C85A32]"
          >
            Richiedi Preventivo
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="md:hidden text-[#1A3322] p-2"
          onClick={() => setOpen((v) => !v)}
          aria-label="Menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="md:hidden overflow-hidden bg-[#F4F2EE]/95 backdrop-blur-xl border-t border-[#1A3322]/10"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  className="font-serif-display text-3xl text-[#1A3322]"
                >
                  {l.label}
                </NavLink>
              ))}
              <Link
                to="/contatti"
                onClick={() => setOpen(false)}
                className="mt-2 inline-flex items-center justify-center rounded-full bg-[#1A3322] text-[#F4F2EE] py-3.5 text-sm uppercase tracking-widest"
              >
                Richiedi Preventivo
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
